/**
 * Practitioner Affiliate Dashboard Screen
 *
 * Screen 3 of the Practitioner Portal v2.
 * Shows earnings summary, referral link, conversion funnel, and founding badge.
 *
 * Architecture:
 *   GlassBackground as root wrapper
 *   Inline back-arrow header (no PractitionerHeader reuse — different pattern)
 *   ScrollView containing all 4 sections
 *
 * Data: usePartnerDashboard() — mock data until affiliate DB schema ships.
 * Analytics: trackEvent() on screen view and user interactions.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  Alert,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import { GlassBackground } from '../../components/glass/GlassBackground';
import { useTabScreenPadding } from '../../components/layout/TabScreenWrapper';
import { usePartnerDashboard } from '../../hooks/usePartnerDashboard';
import {
  PartnerEarningsCard,
  ReferralLinkBox,
  ConversionFunnelChart,
  PartnerBadgeCard,
} from '../../components/screens/partner-dashboard';
import type { FunnelStep } from '../../components/screens/partner-dashboard';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const BACK_BUTTON_HIT_SLOP = { top: 4, bottom: 4, left: 4, right: 4 };

const STAGE_KEYS = [
  'partner.funnel_invited',
  'partner.funnel_signed_up',
  'partner.funnel_subscribed',
  'partner.funnel_active',
] as const;

// ---------------------------------------------------------------------------
// Screen component
// ---------------------------------------------------------------------------

export default function PartnerDashboardScreen() {
  const router = useRouter();
  const { t, i18n } = useTranslation('practitioner');
  const locale = i18n.language;
  const bottomPadding = useTabScreenPadding();

  const {
    earnings,
    referralLink,
    funnel,
    certification,
    copyReferralLink,
    shareReferralLink,
  } = usePartnerDashboard();

  // Track screen view on mount
  useEffect(() => {
    trackEvent('Screen_View_PartnerDashboard', {});
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleCopy = useCallback(async () => {
    trackEvent('Button_Click_CopyReferralLink', {});
    await copyReferralLink();
  }, [copyReferralLink]);

  const handleShareEmail = useCallback(async () => {
    trackEvent('Button_Click_ShareReferralLink', { method: 'email' });
    await shareReferralLink('email');
  }, [shareReferralLink]);

  const handleShareSMS = useCallback(async () => {
    trackEvent('Button_Click_ShareReferralLink', { method: 'sms' });
    await shareReferralLink('sms');
  }, [shareReferralLink]);

  const handleShowQR = useCallback(() => {
    trackEvent('Button_Click_ShareReferralLink', { method: 'qr' });
    // QR bottom sheet deferred — separate ticket for QR library evaluation.
    Alert.alert(t('partner.qr_title'), t('partner.qr_coming_soon'));
  }, [t]);

  const handleDownloadBadge = useCallback(() => {
    trackEvent('Button_Click_DownloadBadge', {});
    // Badge download deferred — requires Expo Sharing + asset generation.
    Alert.alert(t('partner.download_badge'), t('partner.download_coming_soon'));
  }, [t]);

  // ---------------------------------------------------------------------------
  // Build funnel steps with i18n labels
  // ---------------------------------------------------------------------------

  const funnelSteps: FunnelStep[] = useMemo(() =>
    funnel.map((step, index) => {
      const stageKey = STAGE_KEYS[index] ?? 'partner.funnel_invited';
      const localizedLabel = t(stageKey);
      const percentText = step.percentage != null
        ? String(step.percentage)
        : '100';

      return {
        label: localizedLabel,
        count: step.count,
        conversionRate: step.percentage,
        accessibilityLabel: t('partner.funnel_bar_a11y', {
          count: step.count,
          stage: localizedLabel,
          percent: percentText,
        }),
      };
    }),
    [funnel, t]
  );

  // Format certified since date
  const certifiedSinceDate = useMemo(() => {
    const [year, month] = certification.date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, 1);
    return dateObj.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }, [certification.date, locale]);

  const commissionRatePercent = Math.round(earnings.commissionRate * 100);

  return (
    <GlassBackground>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={styles.root}>
        {/* Back-arrow header */}
        <View style={styles.header}>
          <Pressable
            onPress={handleBack}
            style={styles.backButton}
            accessibilityRole="button"
            accessibilityLabel={t('partner.go_back')}
            hitSlop={BACK_BUTTON_HIT_SLOP}
          >
            <Ionicons name="arrow-back" size={20} color="#4B5563" />
          </Pressable>
          <Text style={styles.headerTitle}>{t('partner.header_title')}</Text>
          {/* Spacer to center the title */}
          <View style={styles.backButton} />
        </View>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: bottomPadding }]}
          showsVerticalScrollIndicator={false}
        >
          {/* Earnings hero card */}
          <PartnerEarningsCard
            thisMonthAmount={earnings.thisMonth}
            totalAmount={earnings.total}
            pendingAmount={earnings.pending}
            commissionRate={earnings.commissionRate}
            locale={locale}
            currency="USD"
            thisMonthLabel={t('partner.this_month_label')}
            totalLabel={t('partner.total_earned_label')}
            pendingLabel={t('partner.pending_label')}
            commissionLabel={t('partner.commission_label')}
            commissionPillLabel={t('partner.commission_rate_pill', {
              rate: commissionRatePercent,
            })}
            thisMonthAccessibilityLabel={t('partner.this_month_earnings', {
              amount: new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
              }).format(earnings.thisMonth),
            })}
            totalAccessibilityLabel={t('partner.total_earned_label')}
            pendingAccessibilityLabel={t('partner.pending_label')}
            commissionAccessibilityLabel={t('partner.commission_label')}
          />

          {/* Referral link section */}
          <View style={styles.section}>
            <ReferralLinkBox
              shortLink={referralLink}
              onCopy={handleCopy}
              onShareEmail={handleShareEmail}
              onShareSMS={handleShareSMS}
              onShowQR={handleShowQR}
              title={t('partner.referral_link_title')}
              copyAccessibilityLabel={t('partner.copy_link_hint')}
              shareEmailLabel={t('partner.share_email')}
              shareSMSLabel={t('partner.share_sms')}
              shareQRLabel={t('partner.share_qr')}
            />
          </View>

          {/* Conversion funnel section */}
          <View style={styles.section}>
            <ConversionFunnelChart
              steps={funnelSteps}
              title={t('partner.funnel_title')}
            />
          </View>

          {/* Founding badge section */}
          <View style={styles.section}>
            <PartnerBadgeCard
              certifiedSince={t('partner.certified_since', {
                date: certifiedSinceDate,
              })}
              onDownload={handleDownloadBadge}
              foundingTitle={t('partner.founding_title')}
              foundingBodyText={t('partner.founding_body', {
                rate: commissionRatePercent,
              })}
              downloadLabel={t('partner.download_badge')}
            />
          </View>
        </ScrollView>
      </View>
    </GlassBackground>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: {
    flex: 1,
  } as ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    zIndex: 50,
  } as ViewStyle,
  backButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  headerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#18181B',
    flex: 1,
    textAlign: 'center',
  } as TextStyle,
  scroll: {
    flex: 1,
  } as ViewStyle,
  scrollContent: {
    paddingTop: 8,
    gap: 16,
    paddingBottom: 24,
  } as ViewStyle,
  section: {
    paddingHorizontal: 16,
  } as ViewStyle,
});
